import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getAllBankAccountsByCaseId,
    getBankAccountById,
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
    searchBankAccounts,
    bulkCreateBankAccounts,
    getBankAccountsWithPagination,
    exportBankAccountsToExcel,
    formatBankAccountData,
    parseAttachmentIds,
    validateBankAccountData
} from "../../../api/caseBankAccountDetailsAsset_api";

// =========== GET ALL BANK ACCOUNTS BY CASE ID ===========
export const getAllBankAccountsByCaseIdAction = createAsyncThunk(
    "bankAccount/getAllBankAccountsByCaseId",
    async (caseDetailsID, { rejectWithValue }) => {
        try {
            const result = await getAllBankAccountsByCaseId(caseDetailsID);

            if (result.success) {
                return {
                    caseDetailsID,
                    bankAccounts: result.data,
                    assetOwnedBy: result.assetOwnedBy,
                    count: result.count,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch bank accounts",
                error: error.message
            });
        }
    }
);

// =========== GET SINGLE BANK ACCOUNT BY ID ===========
export const getBankAccountByIdAction = createAsyncThunk(
    "bankAccount/getBankAccountById",
    async (bankAccountID, { rejectWithValue }) => {
        try {
            const result = await getBankAccountById(bankAccountID);

            if (result.success) {
                const parties = result.parties || [];
                const claimants = parties.filter(p => p.type && p.type.toLowerCase() === 'claimant');
                const respondents = parties.filter(p => p.type && p.type.toLowerCase() === 'respondent');

                return {
                    bankAccount: result.data,
                    assetOwnedBy: { claimants, respondents },
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch bank account",
                error: error.message
            });
        }
    }
);

// =========== CREATE BANK ACCOUNT ===========
export const createBankAccountAction = createAsyncThunk(
    "bankAccount/createBankAccount",
    async ({ caseDetailsID, formData }, { rejectWithValue }) => {
        try {
            let dataToSend;


            if (formData instanceof FormData) {
                dataToSend = formData;

                if (!dataToSend.has('caseDetailsID')) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {

                dataToSend = formatBankAccountData({
                    ...formData,
                    caseDetailsID: caseDetailsID || formData.caseDetailsID
                });
            }

            const result = await createBankAccount(dataToSend);

            if (result.success) {
                return {
                    bankAccount: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to create bank account",
                error: error.message
            });
        }
    }
);

// =========== UPDATE BANK ACCOUNT ===========
export const updateBankAccountAction = createAsyncThunk(
    "bankAccount/updateBankAccount",
    async ({ bankAccountID, bankAccountData, caseDetailsID }, { rejectWithValue }) => {
        try {
            let dataToSend;

            if (bankAccountData instanceof FormData) {
                dataToSend = bankAccountData;
                if (!dataToSend.has('caseDetailsID') && caseDetailsID) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {

                dataToSend = formatBankAccountData({
                    ...bankAccountData,
                    caseDetailsID,
                });
            }

            const result = await updateBankAccount(bankAccountID, dataToSend);


            if (result.success) {
                return {
                    bankAccountID,
                    bankAccount: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update bank account",
                error: error.message
            });
        }
    }
);

// =========== DELETE BANK ACCOUNT ===========
export const deleteBankAccountAction = createAsyncThunk(
    "bankAccount/deleteBankAccount",
    async ({ bankAccountID, updatedBy }, { rejectWithValue }) => {
        try {
            const result = await deleteBankAccount(bankAccountID, updatedBy);

            if (result.success) {
                return {
                    bankAccountID,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to delete bank account",
                error: error.message
            });
        }
    }
);

// =========== SEARCH BANK ACCOUNTS ===========
export const searchBankAccountsAction = createAsyncThunk(
    "bankAccount/searchBankAccounts",
    async ({ caseDetailsID, searchCriteria }, { rejectWithValue }) => {
        try {
            const result = await searchBankAccounts(caseDetailsID, searchCriteria);

            if (result.success) {
                return {
                    caseDetailsID,
                    bankAccounts: result.data,
                    count: result.count,
                    searchCriteria,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to search bank accounts",
                error: error.message
            });
        }
    }
);

// =========== BULK CREATE BANK ACCOUNTS ===========
export const bulkCreateBankAccountsAction = createAsyncThunk(
    "bankAccount/bulkCreateBankAccounts",
    async (bankAccountsData, { rejectWithValue }) => {
        try {

            const formattedBankAccounts = bankAccountsData.map(bankAccount => formatBankAccountData(bankAccount));


            for (const bankAccount of formattedBankAccounts) {
                const validation = validateBankAccountData(bankAccount);
                if (!validation.isValid) {
                    return rejectWithValue({
                        message: "Validation failed for one or more bank accounts",
                        errors: validation.errors
                    });
                }
            }

            const result = await bulkCreateBankAccounts(formattedBankAccounts);

            if (result.success) {
                return {
                    bankAccounts: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to bulk create bank accounts",
                error: error.message
            });
        }
    }
);

// =========== GET BANK ACCOUNTS WITH PAGINATION ===========
export const getBankAccountsWithPaginationAction = createAsyncThunk(
    "bankAccount/getBankAccountsWithPagination",
    async ({ caseDetailsID, page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const result = await getBankAccountsWithPagination(caseDetailsID, page, limit);

            if (result.success) {
                return {
                    caseDetailsID,
                    bankAccounts: result.data,
                    pagination: result.pagination,
                    page,
                    limit,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch bank accounts with pagination",
                error: error.message
            });
        }
    }
);

// =========== EXPORT BANK ACCOUNTS TO EXCEL ===========
export const exportBankAccountsToExcelAction = createAsyncThunk(
    "bankAccount/exportBankAccountsToExcel",
    async (caseDetailsID, { rejectWithValue }) => {
        try {
            const result = await exportBankAccountsToExcel(caseDetailsID);

            if (result.success) {
                return {
                    caseDetailsID,
                    blobData: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to export bank accounts",
                error: error.message
            });
        }
    }
);

// =========== FORMAT BANK ACCOUNT DATA (Non-async utility action) ===========
export const formatBankAccountDataAction = (bankAccountData) => ({
    type: "bankAccount/formatBankAccountData",
    payload: formatBankAccountData(bankAccountData)
});

// =========== PARSE ATTACHMENT IDs (Non-async utility action) ===========
export const parseAttachmentIdsAction = (attachmentField) => ({
    type: "bankAccount/parseAttachmentIds",
    payload: parseAttachmentIds(attachmentField)
});

// =========== VALIDATE BANK ACCOUNT DATA (Non-async utility action) ===========
export const validateBankAccountDataAction = (bankAccountData) => ({
    type: "bankAccount/validateBankAccountData",
    payload: validateBankAccountData(bankAccountData)
});

// =========== CLEAR BANK ACCOUNT STATE ===========
export const clearBankAccountStateAction = () => ({
    type: "bankAccount/clearState"
});

// =========== SET SELECTED BANK ACCOUNT ===========
export const setSelectedBankAccountAction = (bankAccount) => ({
    type: "bankAccount/setSelectedBankAccount",
    payload: bankAccount
});

// =========== SET BANK ACCOUNT FILTERS ===========
export const setBankAccountFiltersAction = (filters) => ({
    type: "bankAccount/setFilters",
    payload: filters
});
